import { MP_REF_H, MP_REF_MIN, MP_REF_W } from "./normal-sim.js";

export type SnakeTrailPoint = { x: number; y: number };

export const MP_SNAKE_SCORE_PER_SPAWN = 500;
export const MP_SNAKE_BALLS_PER_SPAWN = 50;
/** Delay after match start before snake trails are drawn or collide (ms). */
export const MP_SNAKE_TRAIL_START_MS = 3000;

const CURSOR_EDGE_INSET_BASE = 22;

export function mpSnakeLayoutScale(): number {
  return MP_REF_MIN / 600;
}

export function mpSnakeCursorRadiusPx(): number {
  return 5 * mpSnakeLayoutScale();
}

export function mpSnakeEdgeInsetPx(): number {
  return Math.max(10, Math.round(CURSOR_EDGE_INSET_BASE * mpSnakeLayoutScale()));
}

function normToRefPx(nx: number, ny: number): { px: number; py: number } {
  return { px: nx * MP_REF_W, py: ny * MP_REF_H };
}

function trailHalfWidthPx(layoutScale: number): number {
  return Math.max(4 * layoutScale, 5);
}

function minPointSpacingPx(layoutScale: number): number {
  return Math.max(2.5 * layoutScale, 3.5);
}

function distPointToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return Math.hypot(px - x1, py - y1);
  let u = ((px - x1) * dx + (py - y1) * dy) / len2;
  u = Math.max(0, Math.min(1, u));
  const qx = x1 + u * dx;
  const qy = y1 + u * dy;
  return Math.hypot(px - qx, py - qy);
}

function distSegmentToSegment(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): number {
  const ux = bx - ax;
  const uy = by - ay;
  const vx = dx - cx;
  const vy = dy - cy;
  const uLen2 = ux * ux + uy * uy;
  const vLen2 = vx * vx + vy * vy;
  if (uLen2 < 1e-12 && vLen2 < 1e-12) return Math.hypot(ax - cx, ay - cy);
  if (uLen2 < 1e-12) return distPointToSegment(ax, ay, cx, cy, dx, dy);
  if (vLen2 < 1e-12) return distPointToSegment(cx, cy, ax, ay, bx, by);

  let t = ((cx - ax) * ux + (cy - ay) * uy) / uLen2;
  let s = ((ax - cx) * vx + (ay - cy) * vy) / vLen2;
  t = Math.max(0, Math.min(1, t));
  s = Math.max(0, Math.min(1, s));
  const ppx = ax + t * ux;
  const ppy = ay + t * uy;
  const qx = cx + s * vx;
  const qy = cy + s * vy;
  return Math.hypot(ppx - qx, ppy - qy);
}

function firstCollidableSegmentIndex(trailRefPx: Array<{ px: number; py: number }>, gracePx: number): number {
  if (trailRefPx.length < 2) return 0;
  let graceLeft = gracePx;
  let firstCheckIndex = trailRefPx.length - 2;
  while (firstCheckIndex > 0 && graceLeft > 0) {
    const a = trailRefPx[firstCheckIndex - 1]!;
    const b = trailRefPx[firstCheckIndex]!;
    graceLeft -= Math.hypot(b.px - a.px, b.py - a.py);
    firstCheckIndex--;
  }
  return firstCheckIndex;
}

function trailRefPxFromNorm(trail: SnakeTrailPoint[]): Array<{ px: number; py: number }> {
  return trail.map((p) => normToRefPx(p.x, p.y));
}

/** Append interpolated norm points; returns newly added points. */
export function appendSnakeTrailNorm(
  trail: SnakeTrailPoint[],
  nx: number,
  ny: number,
  layoutScale: number,
): SnakeTrailPoint[] {
  const added: SnakeTrailPoint[] = [];
  const spacing = minPointSpacingPx(layoutScale);
  const { px: x, py: y } = normToRefPx(nx, ny);

  if (trail.length === 0) {
    const pt = { x: nx, y: ny };
    trail.push(pt);
    added.push(pt);
    return added;
  }

  const last = trail[trail.length - 1]!;
  const lastPx = normToRefPx(last.x, last.y);
  const dx = x - lastPx.px;
  const dy = y - lastPx.py;
  const dist = Math.hypot(dx, dy);
  if (dist < spacing * 0.25) return added;

  const steps = Math.max(1, Math.ceil(dist / spacing));
  for (let s = 1; s <= steps; s++) {
    const t = s / steps;
    const pt = { x: (lastPx.px + dx * t) / MP_REF_W, y: (lastPx.py + dy * t) / MP_REF_H };
    trail.push(pt);
    added.push(pt);
  }
  return added;
}

export function snakeBorderCollisionNorm(nx: number, ny: number, hitRadiusPx: number, layoutScale: number): boolean {
  const inset = mpSnakeEdgeInsetPx();
  const w = MP_REF_W;
  const h = MP_REF_H;
  const { px: cx, py: cy } = normToRefPx(nx, ny);
  const minX = w >= 2 * inset ? inset : w * 0.5;
  const maxX = w >= 2 * inset ? w - inset : w * 0.5;
  const minY = h >= 2 * inset ? inset : h * 0.5;
  const maxY = h >= 2 * inset ? h - inset : h * 0.5;
  return cx - hitRadiusPx < minX || cx + hitRadiusPx > maxX || cy - hitRadiusPx < minY || cy + hitRadiusPx > maxY;
}

export function snakeTrailCollisionNorm(
  nx: number,
  ny: number,
  trail: SnakeTrailPoint[],
  hitRadiusPx: number,
  layoutScale: number,
  selfGrace: boolean,
  sweepPrevNx?: number,
  sweepPrevNy?: number,
  extraGracePx = 0,
): boolean {
  if (trail.length < 4) return false;
  const threshold = hitRadiusPx + trailHalfWidthPx(layoutScale);
  let gracePx = Math.max(48 * layoutScale, hitRadiusPx * 7) + extraGracePx;
  if (selfGrace) {
    gracePx += threshold;
    if (
      sweepPrevNx != null &&
      sweepPrevNy != null &&
      Number.isFinite(sweepPrevNx) &&
      Number.isFinite(sweepPrevNy)
    ) {
      const prev = normToRefPx(sweepPrevNx, sweepPrevNy);
      const cur = normToRefPx(nx, ny);
      gracePx += Math.hypot(cur.px - prev.px, cur.py - prev.py);
    }
  }
  const trailPx = trailRefPxFromNorm(trail);
  const firstCheckIndex = firstCollidableSegmentIndex(trailPx, gracePx);
  const { px: cx, py: cy } = normToRefPx(nx, ny);
  let prevPx: number | undefined;
  let prevPy: number | undefined;
  if (
    sweepPrevNx != null &&
    sweepPrevNy != null &&
    Number.isFinite(sweepPrevNx) &&
    Number.isFinite(sweepPrevNy)
  ) {
    const p = normToRefPx(sweepPrevNx, sweepPrevNy);
    prevPx = p.px;
    prevPy = p.py;
  }

  for (let i = 0; i < firstCheckIndex; i++) {
    const a = trailPx[i]!;
    const b = trailPx[i + 1]!;
    if (distPointToSegment(cx, cy, a.px, a.py, b.px, b.py) < threshold) return true;
    if (
      prevPx != null &&
      prevPy != null &&
      distSegmentToSegment(prevPx, prevPy, cx, cy, a.px, a.py, b.px, b.py) < threshold
    ) {
      return true;
    }
  }
  return false;
}

/** Swept collision along a mover segment against borders and every snake trail. */
export function snakeMoverCollidesNorm(
  prevNx: number,
  prevNy: number,
  nx: number,
  ny: number,
  playerId: string,
  trailsById: Map<string, SnakeTrailPoint[]>,
  hitRadiusPx: number,
  layoutScale: number,
  extraGraceById?: Map<string, number>,
): boolean {
  const spacing = minPointSpacingPx(layoutScale);
  const prevPx = normToRefPx(prevNx, prevNy);
  const curPx = normToRefPx(nx, ny);
  const dist = Math.hypot(curPx.px - prevPx.px, curPx.py - prevPx.py);
  const steps = Math.max(1, Math.ceil(dist / spacing));
  let sx = prevNx;
  let sy = prevNy;

  for (let step = 1; step <= steps; step++) {
    const t = step / steps;
    const qx = prevNx + (nx - prevNx) * t;
    const qy = prevNy + (ny - prevNy) * t;
    if (snakeBorderCollisionNorm(qx, qy, hitRadiusPx, layoutScale)) return true;

    for (const [id, trail] of trailsById) {
      const isSelf = id === playerId;
      const extraGrace = step === steps ? (extraGraceById?.get(id) ?? 0) : 0;
      if (
        snakeTrailCollisionNorm(
          qx,
          qy,
          trail,
          hitRadiusPx,
          layoutScale,
          isSelf,
          sx,
          sy,
          extraGrace,
        )
      ) {
        return true;
      }
    }

    sx = qx;
    sy = qy;
  }

  return false;
}

export function snakePlayerCollidesAnyTrailNorm(
  nx: number,
  ny: number,
  playerId: string,
  trailsById: Map<string, SnakeTrailPoint[]>,
  prevById: Map<string, { x: number; y: number }>,
  hitRadiusPx: number,
  layoutScale: number,
  extraGraceById?: Map<string, number>,
  moverPrevNx?: number,
  moverPrevNy?: number,
): boolean {
  const prev = prevById.get(playerId);
  const fromX = moverPrevNx ?? prev?.x ?? nx;
  const fromY = moverPrevNy ?? prev?.y ?? ny;
  return snakeMoverCollidesNorm(
    fromX,
    fromY,
    nx,
    ny,
    playerId,
    trailsById,
    hitRadiusPx,
    layoutScale,
    extraGraceById,
  );
}
